import React, { useEffect, useState } from "react";
import { api, formatErr, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, TrendingDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { CURRENCY_CODES } from "@/lib/currencies";
import { useI18n } from "@/contexts/I18nContext";

const CATEGORIES = ["إيجار", "رواتب", "كهرباء وماء", "اتصالات وانترنت", "مواصلات ووقود", "صيانة", "قرطاسية", "تسويق ودعاية", "ضرائب ورسوم", "بنك وعمولات", "أخرى"];

export default function Expenses() {
  const { currentCompany } = useAuth();
  const { t } = useI18n();
  const cur = currentCompany?.base_currency || "SYP";
  const [data, setData] = useState({ items: [], total: 0, count: 0 });
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [category, setCategory] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "إيجار", amount: 0, currency: cur, date: new Date().toISOString().slice(0,10), description: "", cost_center: "" });
  const [rates, setRates] = useState({});
  const [costCenters, setCostCenters] = useState([]);
  const currencyOptions = React.useMemo(() => {
    const set = new Set();
    if (currentCompany?.base_currency) set.add(currentCompany.base_currency);
    (currentCompany?.enabled_currencies || []).forEach(c => c && set.add(c));
    Object.keys(rates || {}).forEach(c => c && set.add(c));
    return Array.from(set).sort();
  }, [currentCompany, rates]);

  const load = async () => {
    try {
      const params = {};
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      if (category) params.category = category;
      const { data } = await api.get("/expenses", { params });
      setData(data);
    } catch(e){ toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, [from, to, category, currentCompany?.id]); // eslint-disable-line
  useEffect(() => { api.get("/currencies").then(({ data }) => setRates(data?.rates || {})).catch(() => {}); }, [currentCompany?.id]);
  useEffect(() => { api.get("/cost-centers").then(({ data }) => setCostCenters(data || [])).catch(() => {}); }, [currentCompany?.id]);

  const save = async () => {
    if (+form.amount <= 0) return toast.error(t("exp_toast_amount"));
    try {
      await api.post("/expenses", { ...form, amount: +form.amount });
      toast.success(t("exp_toast_saved"));
      setOpen(false); setForm({ ...form, amount: 0, description: "" }); load();
    } catch(e){ toast.error(formatErr(e)); }
  };

  const del = async (id) => {
    if (!confirm(t("exp_confirm_del"))) return;
    try { await api.delete(`/expenses/${id}`); toast.success(t("deleted")); load(); } catch(e){ toast.error(formatErr(e)); }
  };

  // group by category
  const byCategory = data.items.reduce((acc, e) => { acc[e.category] = (acc[e.category] || 0) + e.amount; return acc; }, {});

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">{t("exp_title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("exp_sub")}</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div><Label className="text-xs">{t("rep_from")}</Label><Input type="date" value={from} onChange={(e)=>setFrom(e.target.value)} className="h-9" /></div>
          <div><Label className="text-xs">{t("rep_to")}</Label><Input type="date" value={to} onChange={(e)=>setTo(e.target.value)} className="h-9" /></div>
          <div><Label className="text-xs">{t("category")}</Label>
            <Select value={category || "all"} onValueChange={(v)=>setCategory(v==="all"?"":v)}>
              <SelectTrigger className="w-44 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("exp_all")}</SelectItem>
                {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={()=>setOpen(true)} data-testid="add-expense-button" className="h-9"><Plus className="w-4 h-4 me-1" /> {t("exp_new")}</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-xs uppercase text-muted-foreground"><TrendingDown className="w-3.5 h-3.5" /> {t("exp_total")}</div>
          <div className="mt-2 text-2xl font-heading font-bold tabular-nums text-destructive" data-testid="expenses-total">{fmtMoney(data.total, cur)}</div>
          <div className="text-xs text-muted-foreground">{data.count} {t("exp_ops")}</div>
        </Card>
        {Object.entries(byCategory).slice(0, 3).map(([c, v]) => (
          <Card key={c} className="p-4">
            <div className="text-xs uppercase text-muted-foreground">{c}</div>
            <div className="mt-2 text-xl font-heading font-bold tabular-nums">{fmtMoney(v, cur)}</div>
          </Card>
        ))}
      </div>

      <div className="border border-border rounded-md bg-card overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="text-start">{t("date")}</TableHead>
            <TableHead className="text-start">{t("category")}</TableHead>
            <TableHead className="text-start">{t("rep_amount")}</TableHead>
            <TableHead className="text-start">{t("exp_desc")}</TableHead>
            <TableHead className="text-start">{t("exp_by")}</TableHead>
            <TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">{t("exp_no")}</TableCell></TableRow>}
            {data.items.map(e => (
              <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                <TableCell className="text-xs">{e.date}</TableCell>
                <TableCell><span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary">{e.category}</span></TableCell>
                <TableCell className="tabular-nums font-bold text-destructive">{fmtMoney(e.amount, e.currency)}</TableCell>
                <TableCell className="text-sm">{e.description || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{e.created_by_name || "—"}</TableCell>
                <TableCell><Button size="sm" variant="ghost" onClick={()=>del(e.id)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="expense-dialog">
          <DialogHeader><DialogTitle>{t("exp_new")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("date")}</Label><Input type="date" value={form.date} onChange={(e)=>setForm({...form, date:e.target.value})} /></div>
            <div>
              <Label>{t("category")}</Label>
              <Select value={form.category} onValueChange={(v)=>setForm({...form, category:v})}>
                <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>{t("rep_amount")}</Label><Input type="number" value={form.amount} onChange={(e)=>setForm({...form, amount:e.target.value})} data-testid="expense-amount-input" className="tabular-nums font-bold text-lg" autoFocus /></div>
            <div>
              <Label>{t("currency")}</Label>
              <Select value={form.currency} onValueChange={(v)=>setForm({...form, currency:v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{currencyOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>{t("exp_desc")}</Label><Input value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} data-testid="expense-description-input" placeholder={t("exp_desc_ph")} /></div>
            {costCenters.length > 0 && (
              <div className="col-span-2">
                <Label>{t("finance_extras")} — {t("rep_group")}</Label>
                <Select value={form.cost_center || "none"} onValueChange={(v)=>setForm({...form, cost_center: v==="none"?"":v})}>
                  <SelectTrigger data-testid="expense-cc-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {costCenters.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-expense-button">{t("save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
